import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "../styles/SelectRaces.css";
import createTeamConfig from "../../config/createTeamConfig.json";

const SelectRaces = ({
  selectedRaces,
  setSelectedRaces,
  races,
  nextStep,
  prevStep,
}) => {
  const { t } = useTranslation();
  const [activeDivision, setActiveDivision] = useState("Pro Series");

  // --- MAPPING CONFIGURATION ---
  const DB_MAPPING = {
    "Pro Series": ["pro series", "primaire", "proseries"],
    "Continental": ["continental", "secondaire", "div2"],
  };

  // --- 1. HELPERS ---
  const checkDivision = (raceDivision, targetGroup) => {
    const cleanRaceDiv = (raceDivision || "").toLowerCase().trim();
    const validValues = DB_MAPPING[targetGroup] || [];
    return validValues.includes(cleanRaceDiv);
  };

  const parseDate = (dateStr) => {
    if (!dateStr) return new Date(0);
    if (dateStr.includes("-") && dateStr.split("-")[0].length === 4) {
      return new Date(dateStr);
    }
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      return new Date(year, month, day);
    }
    return new Date(dateStr);
  };

  // Fonction d'affichage uniquement
  const formatDateForDisplay = (dateStr) => {
    const d = parseDate(dateStr);
    // Si la date est invalide (ex: new Date(0)), on renvoie tel quel ou vide
    if (d.getFullYear() === 1970) return dateStr;

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0"); // Janvier = 0
    const year = d.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const isRacePast = (dateStr) => {
    const raceDate = parseDate(dateStr);
    
    // On définit la date limite à "Demain à 00h00"
    const limitDate = new Date();
    limitDate.setDate(limitDate.getDate() + 1); // On ajoute 1 jour
    limitDate.setHours(0, 0, 0, 0);

    // Si la date de la course est strictement inférieure à demain minuit
    // (c'est-à-dire : hier ou aujourd'hui) -> C'est passé/verrouillé
    return raceDate < limitDate;
  };

  // --- 2. LIMITES ---
  const getLimit = (divisionName) => {
    const configId = divisionName === "Pro Series" ? "2" : "1";
    return Number(createTeamConfig?.racesNumber?.[configId] || 0);
  };
  const currentLimit = getLimit(activeDivision);

  // --- 3. ANALYSE (COMPTEURS) ---
  const selectionAnalysis = useMemo(() => {
    const safeSelected = selectedRaces || [];
    const countByDivision = (list, targetGroup) =>
      list.filter((r) => checkDivision(r.division, targetGroup)).length;

    return {
      totalPro: countByDivision(safeSelected, "Pro Series"),
      totalConti: countByDivision(safeSelected, "Continental"),
    };
  }, [selectedRaces]);

  const currentTotal =
    activeDivision === "Pro Series"
      ? selectionAnalysis.totalPro
      : selectionAnalysis.totalConti;

  // --- 4. LISTE TABLEAU GAUCHE (TOUT AFFICHER) ---
  const displayedRaces = useMemo(() => {
    if (!races) return [];
    return races.filter((r) => checkDivision(r.division, activeDivision));
  }, [races, activeDivision]);

  // --- 5. LISTE DROITE (RECAP) ---
  const displayedSelectedRaces = useMemo(() => {
    const safeSelected = selectedRaces || [];
    return safeSelected
      .filter((r) => checkDivision(r.division, activeDivision))
      .sort((a, b) => parseDate(a.date_start) - parseDate(b.date_start));
  }, [selectedRaces, activeDivision]);

  // --- ACTIONS ---
  const toggleRace = (race) => {
    // Si la course est passée, STOP (on ne touche pas)
    if (isRacePast(race.date_start)) return;

    const isSelected = selectedRaces.some((r) => r.raceId === race.raceId);
    if (isSelected) {
      setSelectedRaces(selectedRaces.filter((r) => r.raceId !== race.raceId));
    } else {
      setSelectedRaces([...selectedRaces, race]);
    }
  };

  // --- NAVIGATION ---
  const isProValid = selectionAnalysis.totalPro === getLimit("Pro Series");
  const isContiValid = selectionAnalysis.totalConti === getLimit("Continental");
  const canProceed = isProValid && isContiValid;

  const handleNext = () => {
    if (activeDivision === "Pro Series") {
      setActiveDivision("Continental");
      window.scrollTo(0, 0);
    } else {
      if (canProceed) nextStep();
    }
  };

  const handleBack = () => {
    if (activeDivision === "Continental") {
      setActiveDivision("Pro Series");
    } else {
      prevStep();
    }
  };

  return (
    <main className="select-races">
      <div className="select-races-shadow-container">
        <div className="select-races-title">
          <h1>{t("swapRacePick.pageTitle", { step: activeDivision })}</h1>
        </div>
      </div>

      <div className="select-races-header">
        <div className="select-races-rules">
          <p>
            {t("swapRacePick.pageHint", {
              count: currentTotal,
              limit: currentLimit,
            })}
          </p>
        </div>
        <div className="select-races-change-step">
          <button onClick={handleBack}>{t("swapRacePick.backBtn")}</button>
          <button
            onClick={handleNext}
            disabled={activeDivision === "Continental" && !canProceed}
            style={{
              opacity: activeDivision === "Continental" && !canProceed ? 0.5 : 1,
              cursor:
                activeDivision === "Continental" && !canProceed
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            {activeDivision === "Pro Series"
              ? t("common.next", "Suivant (Continental)")
              : t("swapRacePick.nextBtn")}
          </button>
        </div>
      </div>

      <div className="select-races-pannel-view">
        {/* --- COLONNE GAUCHE : TABLEAU COMPLET --- */}
        <div className="select-races-choice">
          <div className="select-races-choice-view" style={{ marginBottom: "1rem" }}>
            <p className="select-races-selected-number">
              {activeDivision} :
              <span
                className={
                  currentTotal === currentLimit
                    ? "select-races-selected-races-green"
                    : "select-races-selected-races-red"
                }
              >
                {currentTotal}
              </span>
              /{currentLimit}
            </p>
          </div>

          <div className="select-races-table-container">
            <table>
              <thead>
                <tr>
                  <th>{t("swapRacePick.cols.race")}</th>
                  <th>{t("swapRacePick.cols.date")}</th>
                  <th>{t("swapRacePick.cols.category")}</th>
                  <th>{t("swapRacePick.cols.profile")}</th>
                </tr>
              </thead>
              <tbody>
                {displayedRaces.length > 0 ? (
                  displayedRaces.map((race) => {
                    const isSelected = selectedRaces.some(
                      (r) => r.raceId === race.raceId
                    );
                    const isPast = isRacePast(race.date_start);

                    // --- STYLE DYNAMIQUE ---
                    let rowStyle = { cursor: "pointer" };
                    let rowClass = "";

                    if (isSelected) {
                      // CAS 1: SÉLECTIONNÉ (Jaune)
                      rowStyle.backgroundColor = "yellow"; // Jaune clair
                      rowStyle.fontWeight = "bold";
                      if (isPast) {
                        // CAS 1b: SÉLECTIONNÉ MAIS PASSÉ (Verrouillé)
                        rowStyle.cursor = "not-allowed";
                        rowStyle.opacity = 0.8;
                        rowStyle.color = "#856404"; // Jaune foncé/marron
                      }
                    } else {
                      // CAS 2: NON SÉLECTIONNÉ
                      if (isPast) {
                        // CAS 2b: NON SÉLECTIONNÉ ET PASSÉ (Grisé)
                        rowStyle.backgroundColor = "#e9ecef";
                        rowStyle.color = "#adb5bd";
                        rowStyle.cursor = "not-allowed";
                      }
                    }

                    return (
                      <tr
                        key={race.raceId}
                        onClick={() => toggleRace(race)}
                        style={rowStyle}
                      >
                        <td>
                          <div className="select-races-table-name-container">
                            <img
                              className="select-races-table-flag-icon"
                              src={`${process.env.PUBLIC_URL}/flags/${(
                                race.country || ""
                              )
                                .normalize("NFD")
                                .replace(/[\u0300-\u036f]/g, "")
                                .replace(/ /g, "_")
                                .toLowerCase()}.png`}
                              alt={race.country}
                              width="20"
                              height="20"
                            />
                            <span>
                              {race.name}
                              {/* Icône Cadenas si Passé */}
                              {isPast && (
                                <span style={{ marginLeft: "5px" }}>🔒</span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td>{formatDateForDisplay(race.date_start)}</td>
                        <td>{race.category}</td>
                        <td>{race.profile || race.courseType}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: "center", padding: "20px" }}>
                      Aucune course trouvée.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* --- COLONNE DROITE : RÉCAPITULATIF --- */}
        <div className="select-races-choice-view">
          <h3>
             {t("swapRacePick.selectedFutures", "Sélection")} 
             ({displayedSelectedRaces.length})
          </h3>
          
          <div className="select-races-scroll-container">
            <div className="select-races-line-container">
              {displayedSelectedRaces.map((r) => {
                const isPast = isRacePast(r.date_start);
                return (
                  <div
                    className="select-races-selected-cell"
                    key={r.raceId}
                    style={{
                        // On garde le jaune aussi à droite pour la cohérence si tu veux
                        backgroundColor: isPast ? "yellow" : "white", 
                        opacity: isPast ? 0.7 : 1,
                        borderColor: isPast ? "#ffeeba" : "var(--primary-color)",
                    }}
                  >
                    <div className="select-races-selected-info">
                      <h4 style={{ color: isPast ? "#856404" : "black" }}>
                        {r.name} {isPast && "🔒"}
                      </h4>
                      <p>{formatDateForDisplay(r.date_start)}</p>
                    </div>

                    {!isPast && (
                      <div
                        style={{
                          cursor: "pointer",
                          color: "red",
                          fontWeight: "bold",
                          marginLeft: "10px",
                        }}
                        onClick={() => toggleRace(r)}
                      >
                        X
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default SelectRaces;